import React, { ComponentProps, useMemo } from 'react';
import { Supplier } from '../../graphql/entities';
import SupplierForm from '../../views/supplier/SupplierForm';
import supplierAttributeQuery from 'graphql/queries/supplier/SupplierAttributeConfigListQuery.graphql';
import { useWorkspace, WorkspaceConfig } from '../../graphql/hooks/useWorkspace';
import { useAttributesForWorkspace } from '../../components/Workspace/useAttributesForWorkspace';
import Workspace from '../../components/Workspace';
import { ISupplier, ISupplierEdge } from '../../graphql/schema/p4types';
import { WorkspaceActionButtons } from '../../components/Workspace/WorkspaceActionButtons';
import { useSupplierWorkspaceActions } from '../../actions/supplier/SupplierWorkspaceAction';
import { IDependentValues } from '../../@types/workspaceTypes';
import { Paths } from '../../@types/keyOfNested';
import { useProductWorkspace } from '../product/ProductWorkspace';

const { workspace } = Supplier;

export const ListCellRenderer = (props: { dependentValues: IDependentValues<ISupplierEdge> }) => {
  const { dependentValues } = props;
  if (!dependentValues.rowData || !dependentValues.rowData.node) return <div />;
  const { reference, name } = dependentValues.rowData.node;
  return (
    <div>
      <div className="cell-primary">{reference}</div>
      <div className="cell-data">{name}</div>
    </div>
  );
};

const SUPPLIER_FIELD_NAMES: Array<Paths<ISupplier>> = ['reference', 'name', 'address.line1'];

const SUPPLIER_COLUMN_OVERRIDES = {
  'vatBusinessCategory.description': {
    name: 'VAT Business Category - Description',
  },
  vatRegistrationNumber: {
    name: 'VAT Registration Number',
  },
  'purchaseNominal.accountNo': {
    name: 'Manual Invoice/Credit Nominal - Account No',
  },
  'purchaseNominal.name': {
    name: 'Manual Invoice/Credit Nominal - Name',
  },
  'purchaseNominal.accountType': {
    name: 'Manual Invoice/Credit Nominal - Account Type',
  },
};

const SupplierWorkspaceActions = (props: Omit<ComponentProps<typeof WorkspaceActionButtons>, 'actionButtons'>) => {
  const [actionButtons, actionModals] = useSupplierWorkspaceActions();

  return (
    <>
      {actionModals}
      <WorkspaceActionButtons {...props} actionButtons={actionButtons} />
    </>
  );
};

export const useSupplierWorkspace = ({ workspaceConfig }: { workspaceConfig: WorkspaceConfig<ISupplier> }) => {
  const attributeColumns = useAttributesForWorkspace(supplierAttributeQuery);
  const workspaceProps = useWorkspace<ISupplier>(workspaceConfig, Supplier, {
    columns: SUPPLIER_FIELD_NAMES,
    customColumns: attributeColumns,
    initialSortColumn: 'reference',
    columnOverrides: SUPPLIER_COLUMN_OVERRIDES,
  });

  return useMemo(
    () => ({
      workspaceProps,
    }),
    [workspaceProps]
  );
};

const SupplierWorkspace = (props: WorkspaceConfig<ISupplier>) => {
  const { workspaceProps } = useSupplierWorkspace({
    workspaceConfig: props, //Likely doesn't need all props, but maintaining existing behaviours.
  });

  return (
    <Workspace
      cellRenderer={ListCellRenderer}
      detailView={SupplierForm}
      defaultQuery={workspace}
      duplicate={true}
      {...workspaceProps}
      {...props}
      entity={Supplier}
      workspaceActionButtonsHOC={SupplierWorkspaceActions}
    />
  );
};

export default SupplierWorkspace;
